import { useEffect, useState, useRef } from "react";
import { getExternalWeatherForecast } from "../api";

const FORECAST_CACHE_KEY = 'plantai_weather_forecast_cache';
const FORECAST_CACHE_TTL = 30 * 60 * 1000;

function getWeatherCondition(code) {
  if (code === 0 || code === 1) return '晴';
  if (code === 2) return '多云';
  if (code === 3) return '阴';
  if (code >= 45 && code <= 48) return '雾';
  if (code >= 51 && code <= 57) return '小雨';
  if (code >= 61 && code <= 67) return '雨';
  if (code >= 71 && code <= 77) return '雪';
  if (code >= 80 && code <= 82) return '阵雨';
  if (code >= 85 && code <= 86) return '阵雪';
  if (code >= 95 && code <= 99) return '雷暴';
  return '多云';
}

function mapBackendRow(row) {
  const fullDate = row.date || '';
  const dateObj = fullDate ? new Date(fullDate) : new Date();
  return {
    date: dateObj.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
    fullDate,
    isToday: Boolean(row.isToday),
    highTemp: row.tempMax ?? row.highTemp,
    lowTemp: row.tempMin ?? row.lowTemp,
    humidity: row.humidity ?? row.humidityAvg ?? row.humidity_avg ?? null,
    windSpeed: row.windSpeed != null ? Math.round(row.windSpeed) : null,
    weatherCode: row.weatherCode,
    condition: row.description || getWeatherCondition(row.weatherCode)
  };
}

function readForecastCache(lat, lon) {
  try {
    const raw = localStorage.getItem(FORECAST_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    const key = `${lat.toFixed(2)}|${lon.toFixed(2)}`;
    if (cache.key !== key) return null;
    if (Date.now() - cache.timestamp > FORECAST_CACHE_TTL) return null;
    return cache.data;
  } catch {
    return null;
  }
}

function saveForecastCache(lat, lon, data) {
  try {
    localStorage.setItem(FORECAST_CACHE_KEY, JSON.stringify({
      key: `${lat.toFixed(2)}|${lon.toFixed(2)}`,
      timestamp: Date.now(),
      data
    }));
  } catch { /* ignore */ }
}

export default function useWeatherForecast(latitude, longitude) {
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!latitude || !longitude) {
      setForecast([]);
      setLoading(false);
      return undefined;
    }

    const lat = latitude;
    const lon = longitude;
    const cached = readForecastCache(lat, lon);
    const cacheHasHumidity = Array.isArray(cached)
      && cached.some((d) => d?.humidity != null || d?.humidityAvg != null);
    if (cached?.length && cacheHasHumidity) {
      setForecast(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (!(cached?.length && cacheHasHumidity)) setLoading(true);
        const res = await getExternalWeatherForecast(lat, lon);
        if (controller.signal.aborted) return;

        const rows = res?.data?.data;
        if (Array.isArray(rows) && rows.length > 0) {
          const forecastData = rows.map(mapBackendRow);
          setForecast(forecastData);
          saveForecastCache(lat, lon, forecastData);
        } else if (!(cached?.length && cacheHasHumidity)) {
          setForecast([]);
        }
      } catch (error) {
        if (!controller.signal.aborted && !(cached?.length && cacheHasHumidity)) {
          console.error('获取天气预报失败:', error);
          setForecast([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [latitude, longitude]);

  return { forecast, loading };
}
